import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack
} from '@chakra-ui/react';
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, InputLimits } from '../../contexts/constants';
import { formatLocalDateTime } from '../../utils/utils';
import { getMultilingualDisplayName } from '../../utils/i18nDisplay';
import { supportedLngs } from '../../utils/i18n';
import {
  BilingualName,
  EquipmentCategoryType,
  EquipmentGroupType,
  Errors,
  LanguageCode
} from '../../contexts/types';

/** ステータスフィルター値 */
type StatusFilter = 'all' | 'official' | 'candidate';

/**
 * マスタメンテナンス - 固定資産 画面
 */
const EquipmentGroup: React.FC = () => {
  const { t } = useTranslation();
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext);

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.EquipmentGroup.title');

  /** SUPPORTED_LANGUAGES/supportedLngsに追従する動的言語リスト */
  const langs = Object.keys(supportedLngs) as LanguageCode[];
  /** GraphQLクエリの多言語フィールド選択セット文字列 (例: "ja en") */
  const langFields = langs.join(' ');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "nowrap",
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル(2列目以降はleftの設定が必要) */
  const fixedColCellStyle = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
    boxShadow: "1px 0 0 #ccc",
  }

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 170,
  }

  /** 項目（値）幅 */
  const valueWidth = 475;

  /** 主要装置・機械マスタデータ */
  const [masterData, setMasterData] = useState<EquipmentGroupType[]>([]);

  /** 設備分類マスタデータ */
  const [categories, setCategories] = useState<EquipmentCategoryType[]>([]);

  /** 編集対象 */
  const [editTarget, setEditTarget] = useState<EquipmentGroupType>({});

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** モーダル表示制御用：設備分類入力用のモーダル */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  // （フィルター）設備分類名を管理
  const [filterEquipmentGroupName, setFilterEquipmentGroupName] = useState<string>("");

  // ステータスフィルター
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // 設備分類フィルター
  const [filterCategoryId, setFilterCategoryId] = useState<string>('');

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  /** 設備分類IDから名前を引くMap */
  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) {
      if (c.equipmentCategoryId && c.equipmentCategoryName) {
        map.set(c.equipmentCategoryId, getMultilingualDisplayName(c.equipmentCategoryName));
      }
    }
    return map;
  }, [categories]);

  /** 設備分類IDから表示順を引くMap */
  const categorySortOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    for (let i = 0; i < categories.length; i++) {
      const c = categories[i];
      if (c.equipmentCategoryId) {
        map.set(c.equipmentCategoryId, c.sortOrder ?? i);
      }
    }
    return map;
  }, [categories]);

  /** 仮登録件数 */
  const candidateCount = useMemo(() => {
    return (masterData ?? []).filter(m => m.isCandidate && !m.deleted).length;
  }, [masterData]);

  /** フィルター済みマスタデータ */
  const fillteredMasterData = (masterData ?? []).filter(m => {
    if (!m) {
      return false;
    }

    // 指定した値が含まれているかを返す
    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };

    // ステータスフィルター
    if (statusFilter === 'official' && m.isCandidate) return false;
    if (statusFilter === 'candidate' && !m.isCandidate) return false;

    // 設備分類フィルター
    if (filterCategoryId && m.equipmentCategoryId !== filterCategoryId) return false;

    // 装置・機械名の動的検索フィルター (ja, en等)
    return langs.some((lang) => hasFilterString(m.equipmentGroupName?.[lang], filterEquipmentGroupName));
  }).sort((a, b) => {
    // 設備分類の表示順でソート
    const catSortA = categorySortOrderMap.get(a.equipmentCategoryId ?? '') ?? 99999;
    const catSortB = categorySortOrderMap.get(b.equipmentCategoryId ?? '') ?? 99999;
    if (catSortA !== catSortB) return catSortA - catSortB;
    // 同じ設備分類内は表示順でソート
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });

  /**
   * 画面初期表示
   */
  useEffect(() => {
    const fetch = async () => {
      await Promise.all([fetchMasterData(), fetchCategories()]);
    }

    // 実行
    fetch();
  }, []);

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = async () => {
    onOpenEditModal();
  }

  /**
   * 指定したプロパティ名の値を変更
   * @param propertyName プロパティ名
   * @param value 値
   */
  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * 設備分類マスタ取得
   */
  const fetchCategories = async () => {
    try {
      const payload = {
        query: `
          query {
            getEquipmentCategory(hasDeleted: false) {
              equipmentCategoryId
              equipmentCategoryName {${langFields}}
              sortOrder
            }
          }`
      };

      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentCategory) {
        setCategories(response.data.getEquipmentCategory);
      } else {
        console.error(`Failed to call API getEquipmentCategory. errors = `, response?.errors);
      }
    } catch (error) {
      console.error("Failed. fetchCategories", error);
    }
  }

  /**
   * 対象のマスタデータ取得
   */
  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
          query {
            getEquipmentGroup(hasDeleted: true) {
              equipmentGroupId
              equipmentGroupName {${langFields}}
              equipmentCategoryId
              sortOrder
              isCandidate
              registeredBy
              deleted
              createDate
              updateDate
            }
          }`
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentGroup) {
        // 設備分類マスタ情報
        setMasterData(response?.data?.getEquipmentGroup);
      } else {
        console.error(`Failed to call API getEquipmentGroup. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: TITLE }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `主要装置・機械一覧取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「装置・機械名」リンク押下
   * @param equipmentGroupId 装置・機械ID
   */
  const handleEdit = async (equipmentGroupId?: string | null) => {
    try {
      const payload = {
        query: `
          query getEquipmentGroupById($equipmentGroupId: String) {
            getEquipmentGroupById(equipmentGroupId: $equipmentGroupId) {
              equipmentGroupId
              equipmentGroupName {${langFields}}
              equipmentCategoryId
              sortOrder
              isCandidate
              registeredBy
              deleted
              createDate
              updateDate
            }
          }`,
        variables: { equipmentGroupId }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentGroupById) {
        // 設備分類マスタ情報
        setEditTarget(response?.data?.getEquipmentGroupById);
        // 編集モーダル表示
        onOpenEditModal();
      } else {
        console.error(`Failed to call API getEquipmentGroupById. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchInfoFailed', { name: TITLE }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `主要装置・機械情報取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    // 登録・更新
    if (!await putEditTarget()) {
      setIsSaving(false);
      return;
    }

    // 一覧更新
    await fetchMasterData();

    // モーダル閉じる
    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * 「確定する」ボタン押下（仮登録→正式採用）
   */
  const handlePromote = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    // isCandidate を false にして保存（失敗時はUIを元に戻す）
    const promoted = { ...editTarget, isCandidate: false };

    if (!await putEditTargetWith(promoted, false)) {
      setIsSaving(false);
      return;
    }

    // 成功した場合のみ editTarget を更新
    setEditTarget(promoted);

    toast({
      title: TITLE,
      description: t('common.message.promoteToOfficial'),
      status: 'success',
      duration: 5000,
      isClosable: true,
    });

    // 一覧更新
    await fetchMasterData();

    // モーダル閉じる
    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * バリデーションチェックを行う。
   * @returns true：検証成功、false：検証失敗
   */
  const validate = () => {
    setErrors({});

    const localErrors: Errors = {};

    // 必須項目チェック
    const required = (value?: string | null) => Boolean(value || null);

    // 必須項目チェックとエラー情報設定
    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string, errorKey: string = propertyName) => {
      const requiredResult = required(target?.[propertyName]);

      if (!requiredResult) {
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    }

    let result = true;

    // 装置・機械名の動的バリデーション (ja, en等)
    langs.forEach((lang) => {
      // 最初の文字を大文字にする (例: 'ja' -> 'Ja', 'en' -> 'En')
      const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);

      // エラーキー: 既存仕様通り "equipmentGroupNameJa", "equipmentGroupNameEn" になる
      const errorKey = `equipmentGroupName${CapitalizedLang}`;

      // 翻訳キー: 既存仕様通り 'ja' の時は "field.name"、それ以外は "field.nameEn" などを参照
      const translationKey = lang === 'ja'
        ? 'features.MasterMaintenance.EquipmentGroup.field.name'
        : `features.MasterMaintenance.EquipmentGroup.field.name${CapitalizedLang}`;

      if (!requiredAndSetError(editTarget.equipmentGroupName, lang, t(translationKey), errorKey)) {
        result = false;
      } else if (lang !== 'ja' && editTarget.equipmentGroupName?.[lang] && editTarget.equipmentGroupName[lang]!.length > 100) {
        const tooLongTranslationKey = `features.MasterMaintenance.EquipmentGroup.error.tooLongName${CapitalizedLang}`;
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t(tooLongTranslationKey),
        };
        result = false;
      }
    });

    if (!requiredAndSetError(editTarget, "equipmentCategoryId", t('features.MasterMaintenance.EquipmentCategory.title'))) {
      result = false;
    }

    setErrors(localErrors);
    return result;
  }

  /**
   * 入力したデータを登録・更新
   */
  const putEditTarget = async () => {
    return putEditTargetWith(editTarget, true);
  }

  /**
   * 指定したデータで登録・更新
   * @param target 保存対象データ
   * @param showSuccessToast 成功時のtoast表示（promote時はfalseにして呼び出し元で表示）
   */
  const putEditTargetWith = async (target: EquipmentGroupType, showSuccessToast: boolean) => {
    try {
      // 登録パラメータ
      const input = {
        equipmentGroupId: target.equipmentGroupId,
        // langを使用して動的に追加する
        equipmentGroupName: langs.reduce((acc, lang) => {
          acc[lang] = target.equipmentGroupName?.[lang] || '';
          return acc;
        }, {} as BilingualName),
        equipmentCategoryId: target.equipmentCategoryId,
        sortOrder: (target.sortOrder != null && !isNaN(target.sortOrder)) ? target.sortOrder : 0,
        isCandidate: target.isCandidate ?? false,
        deleted: target.deleted,
        updateDate: target.updateDate,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
          mutation putEquipmentGroup($input: EquipmentGroupInput!) {
            putEquipmentGroup(input: $input) {status, message}
          }
        `,
        variables: {
          input: input,
        }
      });

      if (result?.data?.putEquipmentGroup?.status === "success") {
        if (showSuccessToast) {
          toast({
            title: TITLE,
            description: t('common.message.inputSaved'),
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
        console.log(`Success to call API putEquipmentGroup.`);
        return true;
      } else if (result?.data?.putEquipmentGroup?.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putEquipmentGroup.`);
        return false;
      } else {
        const apiMessage = result?.data?.putEquipmentGroup?.message;
        toast({
          title: TITLE,
          description: apiMessage ? t(apiMessage) : t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putEquipmentGroup. errors = ${JSON.stringify(result?.errors)}`);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putEquipmentGroup. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    // 一覧更新
    await fetchMasterData();
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   */
  const handleCloseComplete = () => {
    // 初期化
    setEditTarget({});
    setErrors({});
  }

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <HStack spacing={4}>
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>

          {/* 設備分類フィルター */}
          <HStack spacing={2}>
            <Text fontWeight="bold" whiteSpace="nowrap">{t('features.MasterMaintenance.EquipmentCategory.title')}:</Text>
            <Select
              value={filterCategoryId}
              onChange={(e) => setFilterCategoryId(e.target.value)}
              placeholder={t('common.label.all')}
              width="220px"
              size="sm"
            >
              {categories.map(c => (
                <option key={c.equipmentCategoryId} value={c.equipmentCategoryId}>
                  {getMultilingualDisplayName(c.equipmentCategoryName)}
                </option>
              ))}
            </Select>
          </HStack>

          {/* ステータスフィルター */}
          <HStack spacing={2}>
            <Text fontWeight="bold" whiteSpace="nowrap">{t('common.field.status')}:</Text>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              width="160px"
              size="sm"
            >
              <option value="all">{t('common.label.all')}</option>
              <option value="official">{t('common.label.official')}</option>
              <option value="candidate">{t('common.label.candidate')}</option>
            </Select>
            {candidateCount > 0 && (
              <Badge colorScheme="orange" fontSize="sm" px={2} py={1}>
                {t('common.label.candidate')}: {candidateCount}{t('common.label.count')}
              </Badge>
            )}
          </HStack>
        </HStack>
        <TableContainer overflowX="auto" overflowY="auto" h="calc(100dvh - 150px)" mt={2}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              {langs.map((lang) => (
                <col key={`col-equipmentGroupName-${lang}`} width={250} />
              ))}
              <col width={200} />
              <col width={80} />
              <col width={120} />
              <col width={120} />
              <col width={280} />
              <col width={280} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                {langs.map((lang) => {
                  // 'ja' の時は既存仕様通り "field.name"、それ以外は "field.nameEn" などを参照（動的ループ）
                  const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                  const translationKey = lang === 'ja'
                    ? 'features.MasterMaintenance.EquipmentGroup.field.name'
                    : `features.MasterMaintenance.EquipmentGroup.field.name${CapitalizedLang}`;
                  return (
                    <Th {...headerCellStyle} {...(lang === 'ja' ? fixedColCellStyle : {})} key={`th-equipmentGroupName-${lang}`}>
                      {t(translationKey)}
                      {lang === 'ja' && (
                        <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterEquipmentGroupName} setFilterValue={setFilterEquipmentGroupName} />
                      )}
                    </Th>
                  );
                })}
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.EquipmentCategory.title')}</Th>
                <Th {...headerCellStyle} textAlign="center">{t('common.label.sortOrder')}</Th>
                <Th {...headerCellStyle}>{t('common.field.status')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.registrationDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fillteredMasterData.map(record => {
                const bgColor = record.deleted ? "gray.100" : record.isCandidate ? "orange.50" : "white";

                return (
                  <Tr role="group" key={record.equipmentGroupId} _hover={{ bg: "gray.200" }}>
                    {langs.map((lang) => (
                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...(lang === 'ja' ? fixedColCellStyle : {})} key={`td-equipmentGroupName-${lang}-${record.equipmentGroupId}`}>
                        {lang === 'ja' ? (
                          <Tooltip label={record.equipmentGroupName?.[lang]} placement="top-start" hasArrow openDelay={500}>
                            <Button
                              variant="link"
                              textDecoration="underline"
                              color="blue.500"
                              onClick={(e) => {
                                e.currentTarget.blur();
                                handleEdit(record.equipmentGroupId)
                              }}
                              sx={{
                                display: 'inline-block',
                                maxW: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {record.equipmentGroupName?.[lang]}
                              {record.isCandidate && <Tag size="sm" colorScheme="orange" ml={1}>{t('common.label.candidate')}</Tag>}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip label={record.equipmentGroupName?.[lang]} placement="top-start" hasArrow openDelay={500}>
                            <Text isTruncated >{record.equipmentGroupName?.[lang]}</Text>
                          </Tooltip>
                        )}
                      </Td>
                    ))}
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>
                      {record.equipmentCategoryId ? categoryNameMap.get(record.equipmentCategoryId) ?? record.equipmentCategoryId : '-'}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">
                      {record.sortOrder ?? 0}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">
                      {record.isCandidate ? (
                        <Tag size="sm" colorScheme="orange">{t('common.label.candidate')}</Tag>
                      ) : (
                        <Tag size="sm" colorScheme="blue">{t('common.label.official')}</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">{record.deleted ? (
                      <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
                    ) : (
                      <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
                    )
                    }</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updateDate)}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box >

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>{t('features.MasterMaintenance.EquipmentGroup.modal.editTitle')}</Text>
              {editTarget.isCandidate && <Tag colorScheme="orange">{t('common.label.candidate')}</Tag>}
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack>

              {/* 装置・機械名（多言語）入力欄: SUPPORTED_LANGUAGES/supportedLngsに追従する動的ループ */}
              {langs.map((lang) => {
                const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                // エラーキー/翻訳キーは既存仕様通り "equipmentGroupNameJa"/"equipmentGroupNameEn" 等になる
                const errorKey = `equipmentGroupName${CapitalizedLang}`;
                const fieldTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.EquipmentGroup.field.name'
                  : `features.MasterMaintenance.EquipmentGroup.field.name${CapitalizedLang}`;
                const placeholderTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.EquipmentGroup.placeholder.name'
                  : `features.MasterMaintenance.EquipmentGroup.placeholder.name${CapitalizedLang}`;

                return (
                  <FormControl isInvalid={errors[errorKey]?.isError} h={70} key={`fc-equipmentGroupName-${lang}`}>
                    <Flex align="start">
                      <FormLabel style={labelStyle}>
                        {t(fieldTranslationKey)}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          value={editTarget.equipmentGroupName?.[lang] || ""}
                          onChange={(event) => { changeEditTarget("equipmentGroupName", { ...editTarget.equipmentGroupName, [lang]: event.target.value }) }}
                          placeholder={t(placeholderTranslationKey)}
                          width={valueWidth}
                          maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                          isInvalid={errors[errorKey]?.isError}
                        />
                        <FormErrorMessage mt={0}>{errors[errorKey]?.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                );
              })}

              <FormControl isInvalid={errors.equipmentCategoryId?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.EquipmentCategory.title')}
                  </FormLabel>
                  <VStack align="left">
                    <Select
                      value={editTarget.equipmentCategoryId || ""}
                      onChange={(event) => { changeEditTarget("equipmentCategoryId", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.EquipmentGroup.placeholder.selectCategory')}
                      width={valueWidth}
                      isInvalid={errors.equipmentCategoryId?.isError}
                    >
                      {categories.map(c => (
                        <option key={c.equipmentCategoryId} value={c.equipmentCategoryId}>
                          {getMultilingualDisplayName(c.equipmentCategoryName)}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage mt={0}>{errors.equipmentCategoryId?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.sortOrder')}
                  </FormLabel>
                  <NumberInput
                    value={editTarget.sortOrder ?? 0}
                    onChange={(_, valueAsNumber) => { changeEditTarget("sortOrder", isNaN(valueAsNumber) ? 0 : valueAsNumber) }}
                    min={0}
                    max={99999}
                    step={100}
                    width={200}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.deleted?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.deleteFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", false);
                        }}>{t('common.label.notDeleted')}</Button>

                      <Switch
                        isChecked={editTarget.deleted}
                        onChange={(event) => { changeEditTarget("deleted", event.target.checked) }}
                        isInvalid={errors.deleted?.isError}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", true)
                        }}>{t('common.label.deleted')}</Button>
                    </HStack>
                    <FormErrorMessage mt={0}>{errors.deleted?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              {editTarget.isCandidate && editTarget.registeredBy && (
                <FormControl>
                  <Flex align="start">
                    <FormLabel style={labelStyle}>
                      {t('common.label.registeredBy')}
                    </FormLabel>
                    <VStack align="left">
                      <Text>{editTarget.registeredBy}</Text>
                    </VStack>
                  </Flex>
                </FormControl>
              )}

              <FormControl isInvalid={errors.createDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.createDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.createDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.updateDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.updateDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.updateDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            {editTarget.isCandidate && editTarget.updateDate && (
              <Button colorScheme="green" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
                e.currentTarget.blur();
                handlePromote();
              }} m={1}>
                {t('common.button.confirmPromote')}
              </Button>
            )}
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default EquipmentGroup;
